import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  User, Mail, Phone, Globe, Edit3, Save, X, CheckCircle2,
} from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { studentService } from "services/student.service";
import type { User as UserType } from "types/auth.types";
import { LoadingSpinner, PageCard, PageCardTitle, ActionButton, Badge } from "components/dashboard";

const C_PRIMARY = "#ff6c00";

// ─── Styled ────────────────────────────────────────────────────────────────────

const Container = styled.div`max-width: 640px;`;

const AvatarSquare = styled.div`
  width: 72px;
  height: 72px;
  background: ${C_PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.6rem;
  font-weight: 900;
  flex-shrink: 0;
`;
const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`;
const InfoLabel = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 600;
  width: 120px;
  flex-shrink: 0;
`;
const InfoValue = styled.span`
  font-size: 0.875rem;
  color: #111;
  font-weight: 600;
`;
const FormGroup = styled.div`margin-bottom: 1rem;`;
const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #374151;
  display: block;
  margin-bottom: 0.4rem;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1.5px solid #e5e7eb;
  font-size: 0.875rem;
  color: #111;
  box-sizing: border-box;
  &:focus { outline: none; border-color: ${C_PRIMARY}; }
  &:disabled { background: #f9fafb; color: #9ca3af; }
`;
const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const BtnRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;
const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #dcfce7;
  color: #16a34a;
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  border-left: 3px solid #16a34a;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", country: "" });

  useEffect(() => {
    studentService.getProfile().then((p) => {
      setProfile(p);
      setForm({ firstName: p.firstName, lastName: p.lastName, phone: p.phone ?? "", country: p.country ?? "" });
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await studentService.updateProfile(form);
      setProfile(updated);
      setEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      alert("Erreur lors de la mise à jour du profil.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setForm({ firstName: profile.firstName, lastName: profile.lastName, phone: profile.phone ?? "", country: profile.country ?? "" });
    }
    setEditing(false);
  };

  const initials = profile
    ? `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase()
    : "?";

  return (
    <DashboardLayout title="Mon profil">
      <Container>
        {loading ? (
          <LoadingSpinner />
        ) : profile ? (
          <>
            {success && (
              <SuccessBanner>
                <CheckCircle2 size={15} /> Profil mis à jour avec succès.
              </SuccessBanner>
            )}

            {/* Avatar + nom */}
            <PageCard style={{ marginBottom: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
                <AvatarSquare>{initials}</AvatarSquare>
                <div>
                  <p style={{ fontWeight: 800, color: "#111", fontSize: "1.1rem" }}>
                    {profile.firstName} {profile.lastName}
                  </p>
                  <p style={{ fontSize: "0.8rem", color: "#9ca3af", marginTop: 2 }}>
                    {profile.email}
                  </p>
                  <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {profile.roles.map((r) => (
                      <Badge key={r} variant="neutral">
                        {r.replace("ROLE_", "")}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </PageCard>

            {/* Informations */}
            <PageCard style={{ marginBottom: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <PageCardTitle>Informations personnelles</PageCardTitle>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", color: C_PRIMARY, fontWeight: 700, fontSize: "0.8rem" }}
                  >
                    <Edit3 size={14} /> Modifier
                  </button>
                )}
              </div>

              {!editing ? (
                <>
                  <InfoRow>
                    <User size={14} color="#9ca3af" />
                    <InfoLabel>Prénom</InfoLabel>
                    <InfoValue>{profile.firstName}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <User size={14} color="#9ca3af" />
                    <InfoLabel>Nom</InfoLabel>
                    <InfoValue>{profile.lastName}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <Mail size={14} color="#9ca3af" />
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>{profile.email}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <Phone size={14} color="#9ca3af" />
                    <InfoLabel>Téléphone</InfoLabel>
                    <InfoValue>{profile.phone || "—"}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <Globe size={14} color="#9ca3af" />
                    <InfoLabel>Pays</InfoLabel>
                    <InfoValue>{profile.country || "—"}</InfoValue>
                  </InfoRow>
                </>
              ) : (
                <>
                  <FormRow>
                    <FormGroup>
                      <Label>Prénom</Label>
                      <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                    </FormGroup>
                    <FormGroup>
                      <Label>Nom</Label>
                      <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <Label>Email</Label>
                    <Input value={profile.email} disabled />
                  </FormGroup>
                  <FormRow>
                    <FormGroup>
                      <Label>Téléphone</Label>
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+33 6 00 00 00 00" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Pays</Label>
                      <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="France" />
                    </FormGroup>
                  </FormRow>
                  <BtnRow>
                    <ActionButton variant="primary" onClick={handleSave} disabled={saving}>
                      {saving ? "Enregistrement…" : <><Save size={13} /> Enregistrer</>}
                    </ActionButton>
                    <ActionButton variant="ghost" onClick={handleCancel} disabled={saving}>
                      <X size={13} /> Annuler
                    </ActionButton>
                  </BtnRow>
                </>
              )}
            </PageCard>

            {/* Compte */}
            <PageCard>
              <PageCardTitle>Compte</PageCardTitle>
              <InfoRow>
                <Mail size={14} color="#9ca3af" />
                <InfoLabel>Membre depuis</InfoLabel>
                <InfoValue>
                  {new Date(profile.createdAt).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}
                </InfoValue>
              </InfoRow>
            </PageCard>
          </>
        ) : null}
      </Container>
    </DashboardLayout>
  );
};

export default ProfilePage;
